/* eslint-disable no-unused-vars */
import Vue from 'vue';
import Router from 'vue-router';
import { STORAGE_TOKENS } from './constants';
import EnviarTEESForm from '@/views/dinacopa/EnviarTEESForm';
import ListaUsuarios from '@/views/configuracion/usuarios/ListaUsuarios';
import FormUsuario from '@/views/configuracion/usuarios/FormUsuario';
import ListaFormulariosAdmin from '@/views/dinacopa/ListaFormulariosAdmin';
import DespacharFormularios from '@/views/dinacopa/DespacharFormularios';

const ListaPersona = () => import ('./views/configuracion/persona/ListaPersona');
const Login = () => import('./views/Login');
const SidenavBase = () => import('./views/SidenavBase');
const CuentaUsuario = () => import ('./views/cuenta/CuentaUsuario');
const FormPersona = () => import ('./views/configuracion/persona/FormPersona');
const ListaFormularios = () => import ('./views/formularios/lista/ListaFormularios');
const FormularioForm = () => import ('./views/formularios/FormularioForm');
const ListaImportadores = () => import ('@/views/configuracion/importadores/ListaImportadores');
const FormImportador = () => import ('@/views/configuracion/importadores/FormImportador');

Vue.use(Router);

const simpleCruds = [
  {
    baseUrl: 'persona',
    listComponent: ListaPersona,
    formComponent: FormPersona,
    formTitle: 'Persona',
    permissoSuffix: 'Persona',
  },
  {
    baseUrl: 'importador',
    listComponent: ListaImportadores,
    formComponent: FormImportador,
    formTitle: 'Importador',
    permissoSuffix: 'Importador',
  },
  {
    baseUrl: 'usuarios',
    listComponent: ListaUsuarios,
    formComponent: FormUsuario,
    formTitle: 'Usuario',
    permissoSuffix: 'Usuario',
  },
];

const emptyComponent = () =>  ({
  render (c) {
    return c('router-view');
  }
});

const genericListPath = (listComponent, baseUrl, permisoSuffix) => ({
  path: '',
  component: listComponent,
  name: `lista-${baseUrl}`,
  props: () => ({
    permisoCrear: `Crear${permisoSuffix}`,
  }),
  meta: { requiresAuth: true },
});

const genericCreateConfigPath = (formComponent, formTitle, baseUrl, permisoSuffix, {pathName, auth} = { auth: true }) => (
  {
    path: 'nuevo',
    component: formComponent,
    name: `crear-${pathName || formTitle}`,
    props: () => ({
      baseUrl: baseUrl,
      title: formTitle,
      permisoEditar: `Crear${permisoSuffix}`,
      permisoEliminar: `Eliminar${permisoSuffix}`
    }),
    meta: {
      requiresAuth: auth,
      noAuth: !auth,
    },
  }
);

const genericEditConfigPath = (formComponent, formTitle, baseUrl, permisoSuffix, {pathName, children} = {}) => {
  const param = 'id' + (pathName ? pathName.replace(/\W/g, '') : '');
  const props = (route) => ({
    id: route.params[param],
    baseUrl: baseUrl,
    title: formTitle,
    permisoEditar: `Editar${permisoSuffix}`,
    permisoEliminar: `Eliminar${permisoSuffix}`
  });
  const editName = `editar-${pathName || formTitle}`;

  return { path: `:${param}`,
    name: !children ? editName : null ,
    component: !children ? formComponent : emptyComponent(),
    props: !children ? props : null,
    meta: { reservasrequiresAuth: true },
    children: children ? [
      {
        path: '',
        name: editName,
        component: formComponent,
        props: props,
        meta: { requiresAuth: true },
      },
    ].concat(children) : null,
  }
};

const genericConfigurationPath = (
  baseUrl,
  listComponent,
  formComponent,
  formTitle,
  permisoSuffix) => ({
  path: `${baseUrl}`,
  component: {
    render (c) {
      return c('router-view');
    }
  },
  children: [
    genericListPath(listComponent, baseUrl, permisoSuffix),
    genericCreateConfigPath(formComponent,formTitle, baseUrl, permisoSuffix),
    genericEditConfigPath(formComponent, formTitle, baseUrl, permisoSuffix)
  ]
});


const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      component: SidenavBase,
      children: [
        {
          path: '/micuenta',
          component: CuentaUsuario,
          name: 'cuenta-usuario',
          props: (route) => ({
            baseUrl: 'usuarios',
          }),
          meta: { requiresAuth: true }
        },
        ...simpleCruds.map(c => genericConfigurationPath(c.baseUrl, c.listComponent, c.formComponent, c.formTitle, c.permissoSuffix)),
        {
          path: 'formulario',
          component: emptyComponent(),
          children: [
            genericListPath(
              ListaFormularios,
              'formularios',
              ''
            ),
            genericEditConfigPath(
              FormularioForm,
              'Formulario',
              'formularios',
              '',
            ),
          ],
        },
        {
          path: 'generar-formulario',
          component: FormularioForm,
          name: 'generar-formulario',
          props: {
            baseUrl: 'vue/formulario',
            title: 'Formulario',
          },
        },
        {
          path: 'enviar-tees',
          component: EnviarTEESForm,
          props: {
            baseUrl: 'formularios/enviar-tees',
            title: 'Formularios en DNA',
          },
        },
        {
          path: 'despachar',
          component: DespacharFormularios,
          props: {
            baseUrl: 'despachar',
            title: 'Despachar formularios',
          },
        },
        {
          path: 'formulario-admin',
          component: emptyComponent(),
          children: [
            genericListPath(
              ListaFormulariosAdmin,
              'formularios',
              ''
            ),
            genericEditConfigPath(
              FormularioForm,
              'Formulario',
              'formularios',
              '',
            ),
          ],
        },
      ],
    },
    {
      path: '/login',
      component: Login,
      name: 'login',
      meta: { noAuth: true },
    },
    {path: '**', redirect: '/'},
  ],
  base: process.env.VUE_APP_BASE_HREF,
});

/**
 * to: el objeto de ruta objetivo al que se está navegando.
 * from: la ruta actual desde la que se navega.
 * next: esta función debe invocarse para resolver la navegacion.
 *
 * https://router.vuejs.org/guide/advanced/navigation-guards.html#global-guards
 */
router.beforeEach((to, from, next) => {
  if (!to.meta.noAuth) {
    const authToken = window.localStorage.getItem(STORAGE_TOKENS);
    if (!authToken) {
      next({ name: 'login' });
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;
